import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { communicationsService, clientsService, emailTemplatesService, clientContactsService, contractsService } from '../services/api';
import { mensagemDoErroDeCarga, campo, campoComEstado } from '../design';
import { useConfirm } from './shared/ConfirmDialog';
import { TONE_TEXT, TONE_BORDER } from '../utils/statusColors';
import { confirmacaoDoComunicado, envioAnteriorDoAssunto, confirmacaoDoReenvio, fraseDoEnviado, chaveDaSelecao, FRASE_DO_RASTRO_PERDIDO, EnvioDaSessao, horaDeBrasilia } from '../utils/email';
import {
  FiltroDoPublico, FILTRO_VAZIO, Esfera, cidadeDoCliente, produtosPorCliente, passaNoPublico, descricaoDoPublico,
  LIMITE_SEM_TESTE, precisaDeTeste, ConteudoDoEnvio, mesmoConteudo, templateFoiEditado, recusaDoLote,
} from '../utils/comunicados';
import PreviaDoComunicado from './comunicados/PreviaDoComunicado';

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    mail: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' /></svg>,
    search: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' /></svg>,
    paperclip: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13' /></svg>,
    send: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5' /></svg>,
    eye: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    warning: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.71-3l-6.93-12a2 2 0 00-3.42 0l-6.93 12a2 2 0 001.71 3z' /></svg>,
    chevron: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' /></svg>,
  };
  return icons[name] || null;
};

interface ClientRow {
  id: number;
  name?: string;
  tradeName?: string;
  companyName?: string;
  status?: string;
  city?: string;
  state?: string;
  segment?: string;
  isPublicSector?: boolean;
  emailGeneral?: string;
  emailFinancial?: string;
  emails?: Array<{ type?: string; email?: string }>;
}

interface AvailableEmail { email: string; label: string; contactName?: string }

const clientLabel = (c: ClientRow) => c.name || c.tradeName || c.companyName || `Cliente #${c.id}`;

// E-mails da aba Dados (cadastro)
const dadosEmailsOf = (c: ClientRow): AvailableEmail[] => {
  const out: AvailableEmail[] = [];
  if (c.emailGeneral?.trim()) out.push({ email: c.emailGeneral.trim(), label: 'Geral (cadastro)' });
  if (c.emailFinancial?.trim()) out.push({ email: c.emailFinancial.trim(), label: 'Financeiro (cadastro)' });
  (c.emails || []).forEach((e) => {
    if (e.email?.trim()) out.push({ email: e.email.trim(), label: `${e.type || 'E-mail'} (cadastro)` });
  });
  return out;
};

// E-mail "principal" para seleção rápida em lote
const primaryEmailOf = (c: ClientRow): string | null => {
  const d = dadosEmailsOf(c);
  return d.length ? d[0].email : null;
};

const emailKey = (clientId: number, email: string) => `${clientId}|${email.trim().toLowerCase()}`;

interface BatchProgress {
  jobId: string;
  status: string;
  total: number;
  completed: number;
  sent?: number;
  percent: number;
  errors: Array<{ id: number; name?: string; error: string }>;
  skipped: Array<{ id: number; name?: string; reason: string }>;
}

export default function ComunicadosTab() {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Destinatários escolhidos: chave `${clientId}|${email}` -> {clientId, email, name}
  const [recipients, setRecipients] = useState<Map<string, { clientId: number; email: string; name: string }>>(new Map());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [contactsByClient, setContactsByClient] = useState<Record<number, Array<{ nome?: string; email?: string; type?: string }>>>({});
  // CONTATOS QUE NÃO CARREGARAM (08/10/2026). A carga dos contatos falhava calada (`.catch(() => [])`): os e-mails de
  // contato sumiam das opções e, com "Só com e-mail" ligado por padrão, o cliente cujo ÚNICO e-mail é de contato SUMIA
  // DA LISTA. O "N e-mail(s) de M cliente(s)" seguia verdadeiro sobre a escolha, mas a escolha era feita sobre menos
  // gente do que existe: número honesto sobre universo errado. O envio não muda (vai exatamente para os marcados); o
  // que muda é a lista dizer o que não sabe: a ausência é desconhecida, e não sabida.
  const [erroDosContatos, setErroDosContatos] = useState<string | null>(null);
  const [contatosQueFalharam, setContatosQueFalharam] = useState<Record<number, string>>({});
  const [loadingContacts, setLoadingContacts] = useState<Set<number>>(new Set());

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Ativo' | 'all'>('Ativo');
  const [onlyWithEmail, setOnlyWithEmail] = useState(true);

  // Campos do template: valores digitados para cada placeholder [MAIUSCULO] do assunto/conteudo.
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const [attachment, setAttachment] = useState<{ filename: string; contentBase64: string; contentType: string } | null>(null);

  // f1 (27/10/2026): três passos. As abas são livres (voltar e conferir é o normal); quem trava é o botão de envio.
  const [passo, setPasso] = useState<1 | 2 | 3>(1);
  // g1: com modelo escolhido, o HTML fica atrás de "Editar HTML"; sem modelo, o texto é o próprio HTML e fica aberto.
  const [editarHtml, setEditarHtml] = useState(false);
  // k1: o público (órgão público/privado, cidade, produto pela assinatura ativa).
  const [filtroDoPublico, setFiltroDoPublico] = useState<FiltroDoPublico>(FILTRO_VAZIO);
  const [produtos, setProdutos] = useState<Map<number, Set<string>>>(new Map());
  const [erroDosContratos, setErroDosContratos] = useState<string | null>(null);
  // h1: o teste que cobre o conteúdo (o servidor confere o hash; aqui é para o botão saber).
  const [teste, setTeste] = useState<{ id: string; conteudo: ConteudoDoEnvio; enviadoPara: string; quando: string } | null>(null);
  const [testando, setTestando] = useState(false);
  const [erroDoTeste, setErroDoTeste] = useState<string | null>(null);
  const [limiteSemTeste, setLimiteSemTeste] = useState(LIMITE_SEM_TESTE);
  const [recusaQuePedeTeste, setRecusaQuePedeTeste] = useState(false);

  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const confirmar = useConfirm();
  // D1 (11/10/2026): o que já saiu NESTA sessão. Ao fim do lote a seleção ficava, e o segundo clique mandava tudo de novo.
  const [enviosDaSessao, setEnviosDaSessao] = useState<EnvioDaSessao[]>([]);
  const [ultimoEnviado, setUltimoEnviado] = useState<EnvioDaSessao | null>(null);
  // D3: o lote cujo rastro se perdeu (o servidor reiniciou). Com a mesma seleção, o envio fica travado.
  const [selecaoDoRastroPerdido, setSelecaoDoRastroPerdido] = useState<string | null>(null);
  // O lote em curso: o assunto e a seleção de quando saiu, para o fim do lote registrar o envio e limpar.
  const loteEmCurso = React.useRef<{ assunto: string; chave: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [cli, tpls] = await Promise.all([
          clientsService.getAll(),
          emailTemplatesService.getAll(),
        ]);
        setClients(Array.isArray(cli) ? cli : cli.data || []);
        setTemplates(Array.isArray(tpls) ? tpls : []);
        await carregarTodosOsContatos();
        await carregarProdutos();
      } catch (e: any) {
        setError('Erro ao carregar clientes/templates: ' + (e?.message || ''));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Polling do progresso do lote
  useEffect(() => {
    if (!batchProgress || batchProgress.status === 'done' || batchProgress.status === 'not_found') return;
    const interval = setInterval(async () => {
      try {
        const p = await communicationsService.getBatchProgress(batchProgress.jobId);
        setBatchProgress((prev) => (prev ? { ...prev, ...p } : null));
        if (p.status === 'done') {
          clearInterval(interval);
          // D1: o que saiu fica registrado na sessão, e a seleção é limpa (o segundo clique não repete o lote).
          const lote = loteEmCurso.current;
          if (lote) {
            const envio = { assunto: lote.assunto, quando: new Date().toISOString(), n: typeof p.sent === 'number' ? p.sent : (p.total || 0) };
            setEnviosDaSessao((prev) => [...prev, envio]);
            setUltimoEnviado(envio);
          }
          loteEmCurso.current = null;
          setRecipients(new Map());
          setSending(false);
        } else if (p.status === 'not_found') {
          clearInterval(interval);
          // D3: era tratado como fim; o botão voltava ativo e a faixa seguia "Enviando...". Agora a seleção do lote
          // perdido trava o envio, e a tela manda conferir no Log.
          setSelecaoDoRastroPerdido(loteEmCurso.current?.chave || null);
          loteEmCurso.current = null;
          setSending(false);
        }
      } catch (e) {
        // mantém o polling; erros transitórios não devem travar
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [batchProgress?.jobId, batchProgress?.status]);

  // Os contatos deste cliente são DESCONHECIDOS: a carga geral falhou e a dele ainda não veio, ou a dele falhou.
  const contatosDesconhecidos = useCallback(
    (c: ClientRow) => c.id in contatosQueFalharam || (!!erroDosContatos && !contactsByClient[c.id]),
    [contatosQueFalharam, erroDosContatos, contactsByClient],
  );

  /** k1: os produtos de cada cliente pelas assinaturas ativas. Falha vira aviso e o filtro de produto sai, não fica mudo. */
  async function carregarProdutos() {
    try {
      const contratos = await contractsService.getAll();
      setProdutos(produtosPorCliente(Array.isArray(contratos) ? contratos : contratos?.data || []));
      setErroDosContratos(null);
    } catch (e) {
      setErroDosContratos(mensagemDoErroDeCarga(e));
    }
  }

  const cidades = useMemo(
    () => Array.from(new Set(clients.filter((c) => statusFilter !== 'Ativo' || c.status === 'Ativo').map(cidadeDoCliente).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [clients, statusFilter],
  );
  const codigosDeProduto = useMemo(() => {
    const todos = new Set<string>();
    produtos.forEach((s) => s.forEach((p) => todos.add(p)));
    return Array.from(todos).sort();
  }, [produtos]);

  const filteredClients = useMemo(() => {
    const s = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (statusFilter === 'Ativo' && c.status !== 'Ativo') return false;
      if (!passaNoPublico(c, filtroDoPublico, produtos)) return false;
      const contacts = contactsByClient[c.id] || [];
      const hasAnyEmail = !!primaryEmailOf(c) || contacts.some((ct) => ct.email?.trim());
      // Sem saber os contatos, não se sabe se o cliente tem e-mail: ele fica na lista, marcado como desconhecido.
      if (onlyWithEmail && !hasAnyEmail && !contatosDesconhecidos(c)) return false;
      if (!s) return true;
      return (
        clientLabel(c).toLowerCase().includes(s) ||
        (c.companyName || '').toLowerCase().includes(s) ||
        (c.city || '').toLowerCase().includes(s) ||
        dadosEmailsOf(c).some((e) => e.email.toLowerCase().includes(s)) ||
        contacts.some((ct) => (ct.email || '').toLowerCase().includes(s) || (ct.nome || '').toLowerCase().includes(s))
      );
    });
  }, [clients, search, statusFilter, onlyWithEmail, contactsByClient, contatosDesconhecidos, filtroDoPublico, produtos]);

  // E-mails disponíveis = contatos (Suporte/Financeiro) + e-mails do cadastro, deduplicados
  const availableEmailsOf = useCallback((c: ClientRow): AvailableEmail[] => {
    const contacts = contactsByClient[c.id] || [];
    const contactEmails: AvailableEmail[] = contacts
      .filter((ct) => ct.email?.trim())
      .map((ct) => ({ email: ct.email!.trim(), label: `${ct.nome || 'Contato'}${ct.type ? ' · ' + ct.type : ''}`, contactName: ct.nome || undefined }));
    const merged = [...contactEmails, ...dadosEmailsOf(c)];
    const seen = new Set<string>();
    const res: AvailableEmail[] = [];
    for (const m of merged) {
      const k = m.email.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      res.push(m);
    }
    return res;
  }, [contactsByClient]);

  /** Todos os contatos, agrupados por cliente (busca e seleção sem carregar um a um). Falha vira aviso, não lista vazia. */
  const carregarTodosOsContatos = async () => {
    try {
      const contacts = await clientContactsService.getAll();
      const byClient: Record<number, Array<{ nome?: string; email?: string; type?: string }>> = {};
      (Array.isArray(contacts) ? contacts : []).forEach((ct: any) => {
        if (!byClient[ct.clientId]) byClient[ct.clientId] = [];
        byClient[ct.clientId].push({ nome: ct.nome, email: ct.email, type: ct.type });
      });
      setContactsByClient(byClient);
      setErroDosContatos(null);
      setContatosQueFalharam({});
    } catch (e) {
      setErroDosContatos(mensagemDoErroDeCarga(e));
    }
  };

  const loadContacts = useCallback(async (clientId: number) => {
    if (contactsByClient[clientId] || loadingContacts.has(clientId)) return;
    setLoadingContacts((prev) => new Set(prev).add(clientId));
    try {
      const data = await clientContactsService.getByClient(clientId);
      setContactsByClient((prev) => ({ ...prev, [clientId]: Array.isArray(data) ? data : [] }));
      setContatosQueFalharam((prev) => { const n = { ...prev }; delete n[clientId]; return n; });
    } catch (e) {
      // Antes: [] calado, e a linha dizia "Nenhum e-mail cadastrado". Agora a linha diz que os contatos não vieram.
      setContatosQueFalharam((prev) => ({ ...prev, [clientId]: mensagemDoErroDeCarga(e) }));
    } finally {
      setLoadingContacts((prev) => { const n = new Set(prev); n.delete(clientId); return n; });
    }
  }, [contactsByClient, loadingContacts]);

  const toggleExpand = (c: ClientRow) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(c.id)) n.delete(c.id);
      else { n.add(c.id); loadContacts(c.id); }
      return n;
    });
  };

  // name = nome do contato (vira {{contactName}}); cai p/ nome do cliente quando o e-mail não é de um contato
  const toggleEmail = (c: ClientRow, em: AvailableEmail) => {
    setRecipients((prev) => {
      const next = new Map(prev);
      const k = emailKey(c.id, em.email);
      if (next.has(k)) next.delete(k);
      else next.set(k, { clientId: c.id, email: em.email.trim(), name: em.contactName || clientLabel(c) });
      return next;
    });
  };

  // Seleção rápida: e-mail principal de cada cliente visível
  const selectVisiblePrimary = () => {
    setRecipients((prev) => {
      const next = new Map(prev);
      filteredClients.forEach((c) => {
        const e = primaryEmailOf(c);
        if (e) next.set(emailKey(c.id, e), { clientId: c.id, email: e, name: clientLabel(c) });
      });
      return next;
    });
  };

  const clearSelection = () => setRecipients(new Map());

  const applyTemplate = (id: string) => {
    setSelectedTemplateId(id);
    const tpl = templates.find((t) => String(t.id) === id);
    if (tpl) {
      setSubject(tpl.subject || '');
      setHtmlContent(tpl.htmlContent || '');
      setFieldValues({});
      setEditarHtml(false);
    }
  };
  const templateEscolhido = templates.find((t) => String(t.id) === selectedTemplateId) || null;

  const handleFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setAttachment({ filename: file.name, contentBase64: base64, contentType: file.type || 'application/octet-stream' });
    };
    reader.readAsDataURL(file);
  }, []);

  const recipientList = useMemo(() => Array.from(recipients.values()), [recipients]);
  const emailCount = recipients.size;
  const clientCount = useMemo(() => new Set(recipientList.map((r) => r.clientId)).size, [recipientList]);
  const countForClient = useCallback((id: number) => recipientList.filter((r) => r.clientId === id).length, [recipientList]);

  // Campos do template: `[[DATA INICIO]]` no assunto/conteudo vira um input aqui em cima.
  //
  // Colchete DUPLO de proposito. Com colchete simples, o `[PNCP]` que o template de alerta
  // de licitacoes usa como tag literal no assunto viraria um campo obrigatorio e travaria
  // aquele envio. Duplo nao colide com nada que exista hoje.
  // Fabrica, nao constante: regex com /g carrega `lastIndex`, e reaproveitar a mesma
  // instancia entre o exec() daqui e o replace() do fillFields pula ocorrencias.
  const fieldRe = () => /\[\[\s*([^[\]]{1,60}?)\s*\]\]/g;

  const templateFields = useMemo(() => {
    const found: string[] = [];
    for (const src of [subject, htmlContent]) {
      const re = fieldRe();
      let m: RegExpExecArray | null;
      while ((m = re.exec(src || '')) !== null) {
        if (!found.includes(m[1])) found.push(m[1]);
      }
    }
    return found;
  }, [subject, htmlContent]);

  const isDateField = (name: string) => /^DATA\b/i.test(name.trim());

  // `YYYY-MM-DD` do input date vira `DD/MM/AAAA` por split de string.
  // Nada de `new Date(...)` aqui: o parse ISO cai em UTC e volta um dia atras no fim da tarde.
  const formatFieldValue = (name: string, raw: string) => {
    if (!isDateField(name)) return raw;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw.trim());
    return m ? `${m[3]}/${m[2]}/${m[1]}` : raw;
  };

  const fillFields = useCallback(
    (text: string) =>
      (text || '').replace(fieldRe(), (whole, name: string) => {
        const raw = fieldValues[name];
        return raw ? formatFieldValue(name, raw) : whole;
      }),
    [fieldValues],
  );

  const missingFields = useMemo(
    () => templateFields.filter((f) => !(fieldValues[f] || '').trim()),
    [templateFields, fieldValues],
  );

  // h1: o conteúdo que sai (assunto e corpo já preenchidos, e o anexo). O teste só vale para ESTE conteúdo.
  const conteudoAtual: ConteudoDoEnvio = useMemo(
    () => ({ assunto: fillFields(subject), corpo: fillFields(htmlContent), anexo: attachment ? { filename: attachment.filename, contentBase64: attachment.contentBase64 } : null }),
    [subject, htmlContent, attachment, fillFields],
  );
  const testeValido = !!teste && mesmoConteudo(teste.conteudo, conteudoAtual);
  const exigeTeste = precisaDeTeste(emailCount, limiteSemTeste);
  const dadosDoModelo = () => ({
    templateId: templateEscolhido ? Number(templateEscolhido.id) : undefined,
    templateName: templateEscolhido?.name || undefined,
    templateEditado: templateFoiEditado(templateEscolhido, subject, htmlContent),
  });

  // As amostras da prévia: os primeiros marcados (um por cliente); sem marcados, o primeiro cliente visível com e-mail.
  const amostras = useMemo(() => {
    const vistos = new Set<number>();
    const daSelecao = recipientList.filter((r) => (vistos.has(r.clientId) ? false : (vistos.add(r.clientId), true))).slice(0, 3);
    if (daSelecao.length) return daSelecao;
    const c = filteredClients.find((x) => primaryEmailOf(x));
    return c ? [{ clientId: c.id, email: primaryEmailOf(c)!, name: clientLabel(c) }] : [];
  }, [recipientList, filteredClients]);

  // i1: os clientes do público que ficam de fora por não ter e-mail nenhum (pulados), para o passo 3 dizer quem.
  const pulados = useMemo(() => clients.filter((c) => {
    if (statusFilter === 'Ativo' && c.status !== 'Ativo') return false;
    if (!passaNoPublico(c, filtroDoPublico, produtos)) return false;
    if (contatosDesconhecidos(c)) return false;
    return !primaryEmailOf(c) && !(contactsByClient[c.id] || []).some((ct) => ct.email?.trim());
  }), [clients, statusFilter, filtroDoPublico, produtos, contactsByClient, contatosDesconhecidos]);

  const enviarTeste = async () => {
    setErroDoTeste(null);
    setTestando(true);
    try {
      const r = await communicationsService.sendTest({
        subject: conteudoAtual.assunto,
        htmlContent: conteudoAtual.corpo,
        attachments: attachment ? [attachment] : undefined,
        ...dadosDoModelo(),
        amostraClientId: amostras[0]?.clientId,
      });
      if (r?.limiteSemTeste) setLimiteSemTeste(r.limiteSemTeste);
      if (!r?.enviado) throw new Error('o servidor não conseguiu mandar o teste');
      setTeste({ id: r.testeId, conteudo: conteudoAtual, enviadoPara: r.enviadoPara, quando: new Date().toISOString() });
      setRecusaQuePedeTeste(false);
    } catch (e: any) {
      setErroDoTeste(e?.response?.data?.message || e?.message || 'erro desconhecido');
    }
    setTestando(false);
  };

  const handleSend = async () => {
    setError('');
    setRecusaQuePedeTeste(false);
    if (emailCount === 0) { setError('Selecione ao menos um e-mail de destino.'); return; }
    if (!subject.trim() || !htmlContent.trim()) { setError('Preencha o assunto e o conteúdo.'); return; }
    // Sem isto o comunicado sai com "[[DATA INÍCIO]]" literal na caixa do cliente.
    if (missingFields.length > 0) {
      setError(`Preencha os campos do template antes de enviar: ${missingFields.join(', ')}.`);
      return;
    }
    if (exigeTeste && !testeValido) { setError(`Acima de ${limiteSemTeste} e-mails, mande o teste deste conteúdo antes.`); return; }
    const assunto = conteudoAtual.assunto;
    const chave = chaveDaSelecao(recipientList);
    if (selecaoDoRastroPerdido && chave === selecaoDoRastroPerdido) { setError(FRASE_DO_RASTRO_PERDIDO); return; }
    // D1: o mesmo assunto já saiu nesta sessão → pergunta antes, dizendo quando e para quantos.
    const anterior = envioAnteriorDoAssunto(enviosDaSessao, assunto);
    if (anterior && !(await confirmar(confirmacaoDoReenvio(anterior)))) return;
    // i1 (27/10/2026): a confirmação é o passo 3 inteiro (contagem, nomes, não ativos, pulados, amostra) e o botão diz o
    // número; o diálogo do D2 repetia só a contagem e saiu.

    setSending(true);
    setUltimoEnviado(null);
    loteEmCurso.current = { assunto, chave };
    try {
      const res = await communicationsService.sendBatch({
        clientIds: [],
        recipients: recipientList,
        subject: conteudoAtual.assunto,
        htmlContent: conteudoAtual.corpo,
        attachments: attachment ? [attachment] : undefined,
        ...dadosDoModelo(),
        testeId: testeValido ? teste!.id : undefined,
      });
      setBatchProgress({
        jobId: res.jobId,
        status: 'processing',
        total: res.total,
        completed: 0,
        sent: 0,
        percent: 0,
        errors: [],
        skipped: [],
      });
    } catch (e: any) {
      // 400 é recusa (a frase do servidor é a final); as do teste pedem "testar de novo" — inclusive a do reinício do
      // servidor entre o teste e o envio, que acontece de verdade em todo deploy e não pode parecer falha genérica.
      const r = recusaDoLote(e);
      setError(r.texto);
      if (r.limiteSemTeste) setLimiteSemTeste(r.limiteSemTeste);
      if (r.testarDeNovo) { setTeste(null); setRecusaQuePedeTeste(true); }
      loteEmCurso.current = null;
      setSending(false);
    }
  };

  if (loading) {
    return <div className='p-6 text-slate-400'>Carregando...</div>;
  }

  const conf = confirmacaoDoComunicado(recipientList, clients, new Set(filteredClients.map((c) => c.id)));
  const podeEnviar = !sending && emailCount > 0 && missingFields.length === 0 && !!subject.trim() && !!htmlContent.trim()
    && (!exigeTeste || testeValido)
    && !(!!selecaoDoRastroPerdido && chaveDaSelecao(recipientList) === selecaoDoRastroPerdido);
  const mostrarHtml = editarHtml || !templateEscolhido;
  const PASSOS: Array<{ n: 1 | 2 | 3; rotulo: string }> = [
    { n: 1, rotulo: '1 · Mensagem' },
    { n: 2, rotulo: '2 · Público' },
    { n: 3, rotulo: '3 · Revisar e enviar' },
  ];
  const trocarFiltro = (f: Partial<FiltroDoPublico>) => setFiltroDoPublico((prev) => ({ ...prev, ...f }));

  return (
    <div className='p-6 space-y-4' data-comunicados-passo={passo}>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div>
          <div className='flex items-center gap-3 mb-1'>
            <Icon name='mail' className='w-6 h-6 text-slate-300' />
            <h1 className='text-xl font-bold text-white'>Comunicados</h1>
          </div>
          <p className='text-sm text-slate-400'>Um e-mail para os contatos dos clientes, com anexo opcional: escreva, escolha o público, revise e envie.</p>
        </div>
        <nav aria-label='Passos' className='inline-flex rounded-lg border border-central-borda overflow-hidden'>
          {PASSOS.map((p) => (
            <button key={p.n} type='button' onClick={() => setPasso(p.n)} aria-current={passo === p.n ? 'step' : undefined}
              className={`px-4 py-2 text-sm ${passo === p.n ? 'bg-central-elevado text-white font-medium' : 'text-slate-400 hover:text-white'}`}>
              {p.rotulo}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div role='alert' className='p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex flex-wrap items-center gap-2' data-erro-do-envio>
          <Icon name='warning' className='w-4 h-4' /> {error}
          {recusaQuePedeTeste && (
            <button type='button' onClick={() => { setPasso(3); void enviarTeste(); }} disabled={testando} className='ml-2 px-3 py-1 rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-xs font-medium' data-testar-de-novo>
              {testando ? 'Testando…' : 'Testar de novo'}
            </button>
          )}
        </div>
      )}

      {passo === 1 && (
        <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
          <div className='rounded-lg border border-central-borda bg-central-cartao p-4 space-y-4'>
            <div>
              <label className='block text-xs font-medium text-slate-400 mb-1'>Modelo</label>
              <select value={selectedTemplateId} onChange={(e) => applyTemplate(e.target.value)} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}>
                <option value=''>— Sem modelo (escrever do zero) —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} {t.type ? `(${t.type})` : ''}</option>
                ))}
              </select>
              <p className='text-[11px] text-slate-500 mt-1'>Variáveis: <code>{'{{contactName}}'}</code> (nome do contato), <code>{'{{clientName}}'}</code>, <code>{'{{clientCompanyName}}'}</code> (empresa), <code>{'{{clientTradeName}}'}</code>, <code>{'{{clientCnpj}}'}</code>, <code>{'{{year}}'}</code>. Campos a preencher: <code>{'[[DATA INÍCIO]]'}</code> vira um campo abaixo; nome começando com <code>DATA</code> abre seletor de data.</p>
            </div>

            {templateFields.length > 0 && (
              <div className='rounded-lg border border-central-borda bg-central-elevado/40 p-3'>
                <div className='flex items-center gap-2 mb-2'>
                  <span className='text-xs font-medium text-slate-200'>Campos do modelo</span>
                  {missingFields.length > 0 && (
                    <span className='text-[11px] text-slate-400'>({missingFields.length} pendente{missingFields.length > 1 ? 's' : ''})</span>
                  )}
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  {templateFields.map((f) => (
                    <div key={f}>
                      <label className='block text-[11px] font-medium text-slate-400 mb-1'>{f}</label>
                      <input
                        type={isDateField(f) ? 'date' : 'text'}
                        value={fieldValues[f] || ''}
                        onChange={(e) => setFieldValues((prev) => ({ ...prev, [f]: e.target.value }))}
                        placeholder={isDateField(f) ? '' : `Valor para ${f}`}
                        className={`w-full rounded-lg px-3 py-2 text-sm ${campoComEstado(!(fieldValues[f] || '').trim() && TONE_BORDER.warning)}`}
                      />
                    </div>
                  ))}
                </div>
                <p className='text-[11px] text-slate-500 mt-2'>
                  O modelo salvo não muda. O valor entra no assunto e no texto só neste envio, e datas saem no formato <code>DD/MM/AAAA</code>.
                </p>
              </div>
            )}

            <div>
              <label className='block text-xs font-medium text-slate-400 mb-1'>Assunto</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder='Ex.: Importante: descontinuação do SNEP Livre — {{clientName}}' className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} />
            </div>

            <div>
              <div className='flex items-center justify-between mb-1'>
                <label className='block text-xs font-medium text-slate-400'>Texto {mostrarHtml ? '(HTML)' : ''}</label>
                {templateEscolhido && (
                  <button type='button' onClick={() => setEditarHtml((v) => !v)} className='text-xs text-slate-300 hover:text-white' data-editar-html>
                    {editarHtml ? 'Fechar o HTML' : 'Editar HTML'}
                  </button>
                )}
              </div>
              {mostrarHtml ? (
                <textarea value={htmlContent} onChange={(e) => setHtmlContent(e.target.value)} placeholder='<p>Olá {{clientName}}, ...</p>' rows={14}
                  className={`w-full rounded-lg px-3 py-2 text-xs font-mono ${campo}`} />
              ) : (
                <p className='text-xs text-slate-400'>O texto é o do modelo "{templateEscolhido?.name}", com os campos acima. A prévia ao lado mostra como ele chega.</p>
              )}
            </div>

            <div>
              <label className='block text-xs font-medium text-slate-400 mb-1'>Anexo (opcional — ex.: diagrama PDF)</label>
              {attachment ? (
                <div className='flex items-center justify-between rounded-lg border border-central-borda bg-central-elevado px-3 py-2'>
                  <span className='text-sm text-slate-300 flex items-center gap-2 truncate'>
                    <Icon name='paperclip' className='w-4 h-4 text-slate-500' /> {attachment.filename}
                  </span>
                  <button type='button' onClick={() => setAttachment(null)} className='text-slate-500 hover:text-red-400' aria-label='Tirar o anexo'>
                    <Icon name='x' className='w-4 h-4' />
                  </button>
                </div>
              ) : (
                <input
                  type='file'
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer?.files?.[0];
                    if (f) handleFile(f);
                  }}
                  className='block w-full text-sm text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-central-elevado file:text-slate-200 file:text-sm'
                />
              )}
            </div>

            <div className='flex justify-end'>
              <button type='button' onClick={() => setPasso(2)} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-white text-sm font-medium'>Continuar para o público</button>
            </div>
          </div>

          {/* g1 + obs. do Heitor: a prévia fica aberta, sem botão, montada pelo servidor. */}
          <PreviaDoComunicado assunto={conteudoAtual.assunto} corpo={conteudoAtual.corpo} amostras={amostras} />
        </div>
      )}

      {passo === 2 && (
        <div className='rounded-lg border border-central-borda bg-central-cartao p-4 space-y-3'>
          <div className='flex flex-wrap items-center gap-2' data-filtros-do-publico>
            <div role='group' aria-label='Esfera' className='inline-flex rounded-lg border border-central-borda overflow-hidden'>
              {([['todas', 'Todos'], ['publico', 'Órgão público'], ['privado', 'Privado']] as Array<[Esfera, string]>).map(([v, r]) => (
                <button key={v} type='button' aria-pressed={filtroDoPublico.esfera === v} onClick={() => trocarFiltro({ esfera: v })}
                  className={`px-3 py-2 text-sm ${filtroDoPublico.esfera === v ? 'bg-central-elevado text-white font-medium' : 'text-slate-400 hover:text-white'}`}>{r}</button>
              ))}
            </div>
            <select aria-label='Cidade' value='' onChange={(e) => { const v = e.target.value; if (v && !filtroDoPublico.cidades.includes(v)) trocarFiltro({ cidades: [...filtroDoPublico.cidades, v] }); }}
              className={`rounded-lg px-2 py-2 text-sm ${campo}`}>
              <option value=''>Cidade…</option>
              {cidades.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select aria-label='Produto' value='' disabled={!!erroDosContratos} onChange={(e) => { const v = e.target.value; if (v && !filtroDoPublico.produtos.includes(v)) trocarFiltro({ produtos: [...filtroDoPublico.produtos, v] }); }}
              className={`rounded-lg px-2 py-2 text-sm ${campo}`}>
              <option value=''>Produto…</option>
              {codigosDeProduto.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className={`rounded-lg px-2 py-2 text-sm ${campo}`}>
              <option value='Ativo'>Ativos</option>
              <option value='all'>Todos os status</option>
            </select>
            <label className='flex items-center gap-2 text-xs text-slate-400 cursor-pointer'>
              <input type='checkbox' checked={onlyWithEmail} onChange={(e) => setOnlyWithEmail(e.target.checked)} />
              Só com e-mail
            </label>
            {[...filtroDoPublico.cidades.map((c) => ({ k: 'cidades' as const, v: c })), ...filtroDoPublico.produtos.map((p) => ({ k: 'produtos' as const, v: p }))].map(({ k, v }) => (
              <span key={k + v} className='inline-flex items-center gap-1 rounded-full border border-central-borda bg-central-elevado px-2 py-0.5 text-xs text-slate-200' data-filtro-escolhido={v}>
                {v}
                <button type='button' aria-label={`Tirar ${v}`} onClick={() => trocarFiltro({ [k]: filtroDoPublico[k].filter((x) => x !== v) } as any)} className='text-slate-400 hover:text-white'>×</button>
              </span>
            ))}
          </div>
          {erroDosContratos && (
            <div role='alert' className='px-3 py-2 rounded-lg border border-amber-600/50 bg-amber-500/10 text-xs text-amber-200' data-contratos='nao-carregaram'>
              As assinaturas não carregaram ({erroDosContratos}): o filtro de produto está desligado. Os demais filtros funcionam.
              <button type='button' onClick={() => void carregarProdutos()} className='ml-2 px-2 py-0.5 rounded bg-central-elevado text-slate-100 font-medium'>Tentar de novo</button>
            </div>
          )}

          <div className='flex flex-wrap items-center gap-2'>
            <div className='relative flex-1 min-w-[240px]'>
              <Icon name='search' className='w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2' />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Buscar cliente, cidade ou e-mail...' className={`w-full rounded-lg pl-9 pr-3 py-2 text-sm ${campo}`} />
            </div>
            <button type='button' onClick={selectVisiblePrimary} className='text-xs text-slate-300 hover:text-white' title='Adiciona o e-mail principal de cada cliente visível'>E-mail principal dos visíveis ({filteredClients.length})</button>
            <button type='button' onClick={clearSelection} className='text-xs text-slate-400 hover:text-slate-300'>Limpar</button>
          </div>

          {erroDosContatos && (
            <div role='alert' className='px-3 py-2 rounded-lg border border-amber-600/50 bg-amber-500/10 text-xs text-amber-200' data-contatos='nao-carregaram'>
              Os contatos dos clientes não carregaram ({erroDosContatos}). Os e-mails de contato não estão como opção, e
              clientes cujo único e-mail é um contato aparecem como "não sei se tem e-mail". O envio vai exatamente para os
              e-mails marcados.
              <button type='button' onClick={carregarTodosOsContatos} className='ml-2 px-2 py-0.5 rounded bg-central-elevado text-slate-100 font-medium'>Tentar de novo</button>
            </div>
          )}

          <p className='text-[11px] text-slate-500'>Clique no cliente para escolher quais e-mails (contatos de Suporte/Financeiro + e-mails do cadastro) receberão.</p>

          <div className='border border-central-borda rounded-lg overflow-hidden'>
            <div className='max-h-[520px] overflow-y-auto divide-y divide-central-borda'>
              {filteredClients.map((c) => {
                const isOpen = expanded.has(c.id);
                const selCount = countForClient(c.id);
                const primary = primaryEmailOf(c);
                const emails = availableEmailsOf(c);
                return (
                  <div key={c.id} className={selCount > 0 ? 'bg-central-elevado/40' : ''}>
                    <button onClick={() => toggleExpand(c)} className='w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-central-elevado'>
                      <Icon name='chevron' className={`w-3.5 h-3.5 text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm text-white truncate'>{clientLabel(c)}</p>
                        <p className='text-xs truncate'>
                          {primary
                            ? <span className='text-slate-500'>{primary}</span>
                            : contatosDesconhecidos(c) && emails.length === 0
                              // ausência DESCONHECIDA: diferente de "sem e-mail", que é fato verificado
                              ? <span className='text-slate-400 italic' data-email-do-cliente='desconhecido'>Não sei se tem e-mail: os contatos não carregaram</span>
                              : <span className='text-yellow-500' data-email-do-cliente='sem-cadastro'>Sem e-mail no cadastro</span>}
                          {c.city && <span className='text-slate-600'> · {c.city}{c.state ? `/${c.state}` : ''}</span>}
                        </p>
                      </div>
                      {selCount > 0 && <span className='shrink-0 text-xs font-medium bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full'>{selCount} e-mail{selCount > 1 ? 's' : ''}</span>}
                    </button>

                    {isOpen && (
                      <div className='px-3 pb-3 pl-9 space-y-1'>
                        {loadingContacts.has(c.id) && !contactsByClient[c.id] && <p className='text-xs text-slate-500 py-1'>Carregando contatos...</p>}
                        {contatosDesconhecidos(c) && !loadingContacts.has(c.id) && (
                          <p role='alert' className='text-xs text-red-300 py-1'>
                            Não consegui carregar os contatos deste cliente ({contatosQueFalharam[c.id] || erroDosContatos}): os e-mails de contato não estão abaixo.
                            <button type='button' onClick={() => { setContatosQueFalharam((prev) => { const n = { ...prev }; delete n[c.id]; return n; }); loadContacts(c.id); }} className='ml-2 underline text-red-200'>Tentar de novo</button>
                          </p>
                        )}
                        {emails.length === 0 && !loadingContacts.has(c.id) && !contatosDesconhecidos(c) && <p className='text-xs text-yellow-500 py-1'>Nenhum e-mail cadastrado para este cliente.</p>}
                        {emails.map((em) => {
                          const k = emailKey(c.id, em.email);
                          const checked = recipients.has(k);
                          return (
                            <label key={k} className='flex items-center gap-2 py-1 cursor-pointer'>
                              <input type='checkbox' checked={checked} onChange={() => toggleEmail(c, em)} />
                              <span className='text-sm text-slate-200 truncate'>{em.email}</span>
                              <span className='text-[11px] text-slate-500 truncate'>{em.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredClients.length === 0 && (
                <div className='px-3 py-8 text-center text-sm text-slate-500'>Nenhum cliente encontrado.</div>
              )}
            </div>
          </div>

          <div className='flex items-center justify-between'>
            <div className='text-sm text-slate-400'>
              <span className='text-white font-semibold'>{emailCount}</span> e-mail(s) de <span className='text-white font-semibold'>{clientCount}</span> cliente(s)
            </div>
            <button type='button' onClick={() => setPasso(3)} disabled={emailCount === 0} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-white text-sm font-medium disabled:opacity-50'>Continuar para revisar</button>
          </div>
        </div>
      )}

      {passo === 3 && (
        <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
          <div className='rounded-lg border border-central-borda bg-central-cartao p-4 space-y-3' data-revisao>
            <div className='text-2xl font-semibold text-white' data-contagem-do-lote>{emailCount} e-mail(s) · {clientCount} cliente(s)</div>
            <div className='text-sm text-slate-400' data-descricao-do-publico>Público: {descricaoDoPublico(filtroDoPublico, statusFilter)}</div>
            {emailCount > 0 && (
              <div className='text-sm text-slate-300' data-confirmacao-do-lote>
                <p>{conf.message}</p>
                <p className='text-slate-400'>{conf.consequence}</p>
              </div>
            )}
            {pulados.length > 0 && (
              <details className='text-sm text-slate-400' data-pulados>
                <summary className='cursor-pointer'>{pulados.length} cliente(s) do público sem e-mail ficam de fora (ver)</summary>
                <p className='mt-1 text-xs text-slate-500'>{pulados.map(clientLabel).join(', ')}</p>
              </details>
            )}
            <div className='text-sm text-slate-400'>Anexo: {attachment ? attachment.filename : 'nenhum'}</div>
            <div className='text-sm text-slate-400'>Modelo: {templateEscolhido ? `${templateEscolhido.name}${templateFoiEditado(templateEscolhido, subject, htmlContent) ? ' (texto editado)' : ''}` : 'sem modelo'}</div>
            {missingFields.length > 0 && (
              <p className={`text-xs ${TONE_TEXT.warning}`}>Falta preencher: {missingFields.join(', ')}</p>
            )}

            <div className='border-t border-central-borda pt-3 space-y-2' data-teste-do-comunicado>
              <div className='flex flex-wrap items-center gap-3'>
                <button type='button' onClick={() => void enviarTeste()} disabled={testando || !subject.trim() || !htmlContent.trim() || missingFields.length > 0}
                  className='px-3 py-2 rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-sm disabled:opacity-50'>
                  {testando ? 'Enviando o teste…' : 'Enviar teste para mim'}
                </button>
                {testeValido && teste && (
                  <span className={`text-sm ${TONE_TEXT.success}`} data-teste='valido'>Teste enviado às {horaDeBrasilia(teste.quando)} para {teste.enviadoPara}</span>
                )}
                {teste && !testeValido && (
                  <span className={`text-sm ${TONE_TEXT.warning}`} data-teste='desatualizado'>O texto mudou depois do teste: teste de novo.</span>
                )}
              </div>
              {exigeTeste && !testeValido && (
                <p className='text-xs text-slate-400' data-teste='obrigatorio'>Acima de {limiteSemTeste} e-mails, o envio só libera depois do teste deste conteúdo.</p>
              )}
              {erroDoTeste && <p role='alert' className='text-xs text-red-300'>O teste não saiu: {erroDoTeste}</p>}
            </div>

            {batchProgress && (
              <div className='p-4 rounded-lg border border-central-borda bg-central-elevado/40'>
                <p className='font-medium text-white mb-2'>
                  {batchProgress.status === 'done' ? 'Envio concluído' : batchProgress.status === 'not_found' ? 'Acompanhamento perdido' : 'Enviando comunicados...'}
                </p>
                {batchProgress.status === 'done' && ultimoEnviado && (
                  <p className={`text-sm mb-2 ${TONE_TEXT.success}`} data-testid='enviado'>{fraseDoEnviado(ultimoEnviado.quando, ultimoEnviado.n)}</p>
                )}
                {batchProgress.status === 'not_found' && (
                  <p role='alert' className={`text-sm mb-2 ${TONE_TEXT.warning}`} data-testid='rastro-perdido'>{FRASE_DO_RASTRO_PERDIDO}</p>
                )}
                <div className='w-full bg-central-borda rounded-full h-2 overflow-hidden'>
                  <div className='bg-primary h-full transition-all duration-300' style={{ width: `${batchProgress.percent}%` }} />
                </div>
                <p className='text-sm text-slate-400 mt-2'>
                  {batchProgress.completed}/{batchProgress.total} ({batchProgress.percent}%)
                  {typeof batchProgress.sent === 'number' && <span className='text-green-400'> · {batchProgress.sent} enviado(s)</span>}
                  {batchProgress.errors.length > 0 && <span className='text-red-400'> · {batchProgress.errors.length} erro(s)</span>}
                  {batchProgress.skipped.length > 0 && <span className='text-yellow-400'> · {batchProgress.skipped.length} pulado(s)</span>}
                </p>
                {(batchProgress.errors.length > 0 || batchProgress.skipped.length > 0) && batchProgress.status === 'done' && (
                  <div className='mt-2 text-xs text-slate-500 max-h-24 overflow-y-auto'>
                    {batchProgress.skipped.map((s, i) => <div key={'s' + i}>Pulado: {s.name || '#' + s.id} — {s.reason}</div>)}
                    {batchProgress.errors.map((e, i) => <div key={'e' + i} className='text-red-400'>Erro: {e.name || '#' + e.id} — {e.error}</div>)}
                  </div>
                )}
              </div>
            )}

            <div className='flex justify-end'>
              <button type='button' onClick={handleSend} disabled={!podeEnviar} data-enviar-o-lote
                className='flex items-center gap-2 bg-primary hover:bg-primary-strong disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-5 py-2.5 rounded-lg text-sm'>
                <Icon name='send' className='w-4 h-4' />
                {sending ? 'Enviando...' : `Enviar para ${emailCount} e-mail${emailCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>

          {/* i1: a amostra de até 3 destinatários reais, montada pelo servidor como cada um vai receber. */}
          <PreviaDoComunicado titulo='Amostra' assunto={conteudoAtual.assunto} corpo={conteudoAtual.corpo} amostras={amostras} />
        </div>
      )}
    </div>
  );
}
